/**
 * AuthModalContext - lets any component open the shared AuthModal.
 *
 * Rendered once at the app root. Usage:
 *   const { openAuthModal } = useAuthModal();
 *   openAuthModal({ initialMode: 'signup', title: 'Start planning', returnTo: '/user/dashboard' });
 *
 * On success the modal closes, AuthContext is already updated by AuthForm, and
 * we honour options.onSuccess -> options.returnTo -> dashboard (in that order).
 */

import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import AuthModal from '../components/auth/AuthModal';

const AuthModalContext = createContext(null);

const CLOSED = { open: false, initialMode: 'login', title: null, subtitle: null, returnTo: null, onSuccess: null };

export const AuthModalProvider = ({ children }) => {
  const navigate = useNavigate();
  const [state, setState] = useState(CLOSED);

  const openAuthModal = useCallback((options = {}) => {
    setState({
      open: true,
      initialMode: options.initialMode || 'login',
      title: options.title || null,
      subtitle: options.subtitle || null,
      returnTo: options.returnTo || null,
      onSuccess: options.onSuccess || null,
    });
  }, []);

  const closeAuthModal = useCallback(() => setState((s) => ({ ...s, open: false })), []);

  const handleSuccess = useCallback(
    (user) => {
      const { returnTo, onSuccess } = state;
      setState((s) => ({ ...s, open: false }));
      if (onSuccess) {
        onSuccess(user);
        return;
      }
      if (returnTo) {
        navigate(returnTo);
        return;
      }
      const isAdminUser = user?.role === 'admin' || user?.role === 'super_admin';
      navigate(isAdminUser ? '/admin/dashboard' : '/user/dashboard');
    },
    [state, navigate]
  );

  const value = useMemo(() => ({ openAuthModal, closeAuthModal }), [openAuthModal, closeAuthModal]);

  return (
    <AuthModalContext.Provider value={value}>
      {children}
      <AuthModal
        open={state.open}
        onClose={closeAuthModal}
        initialMode={state.initialMode}
        title={state.title}
        subtitle={state.subtitle}
        onSuccess={handleSuccess}
      />
    </AuthModalContext.Provider>
  );
};

export const useAuthModal = () => {
  const ctx = useContext(AuthModalContext);
  if (!ctx) throw new Error('useAuthModal must be used within an AuthModalProvider');
  return ctx;
};

export default AuthModalContext;
